import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Datastore } from '@freelancer/datastore';
import { MembershipBenefitsCollection } from '@freelancer/datastore/collections';
import {
  FileStorage,
  StorageBucket,
  TaskState,
} from '@freelancer/file-storage';
import { ImageFileType } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import {
  CalloutComponent,
  CalloutPlacement,
  CalloutSize,
} from '@freelancer/ui/callout';
import { ImageCropperAspectRatio } from '@freelancer/ui/image-cropper';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import {
  allowedFileTypes,
  maxFileSize,
  required,
} from '@freelancer/ui/validators';
import {
  COVER_IMAGE_HEIGHTApi,
  COVER_IMAGE_MAX_DIMENSIONApi,
  COVER_IMAGE_WIDTHApi,
} from 'api-typings/users/users';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ImageDimensions,
  ImageUploadParams,
  UserProfilePictureSelectorComponent,
} from '../user-profile-picture/user-profile-picture-selector/user-profile-picture-selector.component';

@Component({
  selector: 'app-user-profile-edit-cover-picture',
  template: `
    <fl-callout
      class="EditButton"
      #callout
      i18n-mobileHeaderTitle="Upload Cover Photo callout title"
      mobileHeaderTitle="Upload Cover Photo"
      [mobileFullscreen]="true"
      [placement]="CalloutPlacement.RIGHT"
      [size]="CalloutSize.MEDIUM"
    >
      <fl-callout-trigger>
        <fl-button
          flTrackingLabel="UserProfileEditCoverImageUpload"
          i18n="User profile upload cover photo button"
          [color]="ButtonColor.DEFAULT"
          [size]="ButtonSize.SMALL"
        >
          Upload Cover Photo
        </fl-button>
      </fl-callout-trigger>

      <fl-callout-content>
        <ng-container
          *ngIf="
            hasCoverImageBenefits$ | flAsync;
            else invalidCoverPhotoBenefit
          "
        >
          <app-user-profile-picture-selector
            #pictureSelector
            header="Edit Cover Picture"
            i18n-header="Edit cover picture label"
            detail="Recommended size: {{
              imageDimensions.minParams.width
            }}px x {{ imageDimensions.minParams.height }}px"
            i18n-detail="Picture properties label"
            extraDetail="Supported file formats: PNG, JPG and GIF"
            i18n-extraDetail="Supported file format label"
            uploadButtonText="Set as Cover Photo"
            i18n-uploadButtonText="Upload cover picture button text"
            [aspectRatio]="ImageCropperAspectRatio.BACKGROUND_COVER_PHOTO"
            [changeImageMode$]="changeImageMode$"
            [control]="control"
            [getRawImage]="false"
            [hasImage$]="hasAvatar$"
            [imageDimensions]="imageDimensions"
            [showImageCropper$]="showImageCropper$"
            [uploadTaskError$]="uploadTaskError$"
            [uploadTaskProgress$]="uploadTaskProgress$"
            [uploadTaskState$]="uploadTaskState$"
            (uploadPhoto)="handleUploadPhoto($event)"
            (imageCropped)="handleImageCropped($event)"
            (imageReset)="handleImageReset()"
          ></app-user-profile-picture-selector>
        </ng-container>

        <ng-template #invalidCoverPhotoBenefit>
          <fl-text
            i18n="Subscribe to paid membership header"
            [weight]="FontWeight.BOLD"
            [size]="TextSize.SMALL"
            [flMarginBottom]="Margin.XSMALL"
          >
            Subscribe to Paid Membership
          </fl-text>

          <fl-text i18n="Subscribe to paid membership main body">
            You must subscribe to a paid membership plan if you wish to change
            your cover picture.
          </fl-text>
          <fl-link
            flTrackingLabel="UserProfileEditCoverImageMembershipSupportLink"
            i18n="Subscribe to paid membership support link"
            link="/membership"
          >
            Learn More
          </fl-link>
        </ng-template>
      </fl-callout-content>
    </fl-callout>
  `,
  styleUrls: ['./user-profile-edit-cover-picture.component.scss'],
})
export class UserProfileEditCoverPictureComponent implements OnInit, OnDestroy {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CalloutPlacement = CalloutPlacement;
  CalloutSize = CalloutSize;
  FontColor = FontColor;
  TextSize = TextSize;
  FontWeight = FontWeight;
  ImageCropperAspectRatio = ImageCropperAspectRatio;
  Margin = Margin;

  showImageCropper$ = Rx.of(true);
  changeImageMode$ = Rx.of(false);
  hasAvatar$ = Rx.of(false);

  hasCoverImageBenefits$: Rx.Observable<boolean>;
  private uploadTaskErrorSubject$ = new Rx.BehaviorSubject<boolean>(false);
  uploadTaskError$ = this.uploadTaskErrorSubject$.asObservable();
  uploadTaskProgress$: Rx.Observable<number>;
  uploadTaskState$: Rx.Observable<TaskState | undefined>;
  uploadSubscription?: Rx.Subscription;

  imageDimensions: ImageDimensions = {
    minParams: {
      width: COVER_IMAGE_WIDTHApi,
      height: COVER_IMAGE_HEIGHTApi,
    },
    maxParams: {
      width: COVER_IMAGE_MAX_DIMENSIONApi,
      height: COVER_IMAGE_MAX_DIMENSIONApi,
    },
  };

  control = new FormControl('', [
    required($localize`Please select a cover image.`),
    maxFileSize(
      1024 * 1024 * 2,
      $localize`Maximum file size is 2MB, please compress or try another image.`,
    ),
    allowedFileTypes(
      [
        ImageFileType.JPG,
        ImageFileType.JPEG,
        ImageFileType.PNG,
        ImageFileType.GIF,
      ],
      $localize`Invalid image format, please upload a PNG, JPG or GIF.`,
    ),
  ]);

  @ViewChild('callout') callout: CalloutComponent;
  @ViewChild('pictureSelector')
  pictureSelector: UserProfilePictureSelectorComponent;

  @Output() imageCropped = new EventEmitter<string>();
  @Output() imageReset = new EventEmitter<string>();

  constructor(private datastore: Datastore, private fileUpload: FileStorage) {}

  ngOnInit() {
    this.hasCoverImageBenefits$ = this.datastore
      .document<MembershipBenefitsCollection>(
        'membershipBenefits',
        'cover_photo',
      )
      .valueChanges()
      .pipe(map(benefit => benefit.value === 1));
  }

  handleUploadPhoto(imageUploadParams: ImageUploadParams): void {
    this.unsubscribe();

    const uploadTask = this.fileUpload.upload(
      StorageBucket.PROFILE_COVER_IMAGES,
      imageUploadParams.file,
      {
        customMetadata: {
          x: imageUploadParams.cropper.x.toString(),
          y: imageUploadParams.cropper.y.toString(),
          crop_width: imageUploadParams.cropper.width.toString(),
          crop_height: imageUploadParams.cropper.height.toString(),
        },
      },
    );

    this.uploadTaskState$ = uploadTask.changes().pipe(map(task => task.state));

    this.uploadSubscription = uploadTask.changes().subscribe(_uploadTask => {
      if (_uploadTask.state === 'error') {
        // On error the UI state should be reset and an error message should be
        // shown.
        this.pictureSelector.reset();
        this.uploadTaskErrorSubject$.next(true);
        return;
      }

      if (_uploadTask.state === 'success') {
        // On success the callout should be closed and the data should be reset,
        // so if the user wants to change the picture again, they have a fresh
        // state.
        this.pictureSelector.reset();
        this.callout.close();
      }
    });

    this.uploadTaskProgress$ = uploadTask.percentageChanges();
  }

  // If the user is editing the image, once the it has been cropped this will
  // set the current image as the cropped image. This enables a live view of
  // what the image will look like.
  handleImageCropped(base64: string | null): void {
    if (!base64) {
      return;
    }
    this.imageCropped.emit(base64);
  }

  handleImageReset(): void {
    this.imageReset.emit();
  }

  unsubscribe(): void {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }
}
